import {
  AccessCredentialsScopePermission,
  AccessCredentialsScopeName,
  scopePermissionDict,
} from "api/entities/access-credential";
import { IEntityMetricsResponse } from "api/entities/metrics";
import {
  IShardRepositoryCredentialsConfirmationData,
  IShardRepositoryRaw,
  IShardRepositoryTestConnectionRequest,
  IShardRepositoryTestConnectionResponse,
  IShardRepositoryUpdateConfirmationData,
  IShardRepository,
  StorageType,
} from "api/entities/shard-repository";
import { shardRepositoryService } from "api/services/shard-repository-service";
import DetailsPanel from "components/shared-ui/details-panel/details-panel";
import { FormPanel } from "components/shared-ui/form/form-panel/form-panel";
import { IShardClusterRepository } from "components/shard-clusters/sections/storage-nodes-section/shard-repositories-section";
import { OwnerInfoSection } from "components/common/sections/details-owner-info/details-owner-info";
import {
  IGCSConfigProps,
  IShardRepositoryForm,
  IShardRepositoryFormValue,
  IShardRepositoryTypes,
  ShardRepositorySection,
} from "components/shard-repositories/sections/shard-repository-section/shard-repository-section";
import { Formatters } from "helper/formatters";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { Action } from "redux";
import { ThunkDispatch } from "redux-thunk";
import { catchException, commonActions } from "redux/actions/common";
import { storagePoolActions } from "redux/actions/shard-cluster";
import { IAppState } from "redux/reducers";
import { enumUtils } from "utils/enum-utils";
import { formUtils, useField } from "utils/form-utils";
import { useApi } from "utils/hooks/use-api";
import { storageUtils } from "utils/storage-utils";
import { validators } from "utils/validators";
import styles from "./shard-repository-panel.module.scss";
import { MetricsInfo } from "components/common/sections/metrics-info/metrics-info";
import { UpdateConfirmation } from "components/shard-repositories/dialogs/update-confirmation/update-confirmation";
import { shardRepositoryActions } from "redux/actions/shard-repository";

function mapDispatchToProps(dispatch: ThunkDispatch<IAppState, void, Action>) {
  return {
    showSnackBar: (message: string) => {
      return dispatch(commonActions.showSnackBar(true, message));
    },
    openCredentialsInfo: (data: IShardRepositoryCredentialsConfirmationData) =>
      dispatch(storagePoolActions.credentialsInfo.open(data)),
    openUpdateConfirmation: (data: IShardRepositoryUpdateConfirmationData) =>
      dispatch(shardRepositoryActions.updateConfirmation.open(data)),
  };
}

export enum ShardRepositoryPanelMode {
  Add = 1,
  FullEdit = 2,
  LimitedEdit = 3,
  SensitiveFieldsUpdate = 4,
}

export interface IShardRepositoryPanelProps extends ReturnType<
  typeof mapDispatchToProps
> {
  shardClusterRepository?: IShardClusterRepository;
  mode: ShardRepositoryPanelMode;
  onClose: () => void;
  onRepositorySaved: (
    node: IShardRepository | IShardRepositoryRaw,
    storageNodeTypes: IShardRepositoryTypes,
    testConnectionResult: boolean,
  ) => void;

  accessCredentialsScope?: AccessCredentialsScopeName;
}

const modeToTitleMap = {
  [ShardRepositoryPanelMode.Add]: "Add a Shard Repository",
  [ShardRepositoryPanelMode.SensitiveFieldsUpdate]: "Update a Shard Repository",
  [ShardRepositoryPanelMode.FullEdit]: "Manage Shard Repository",
  [ShardRepositoryPanelMode.LimitedEdit]: "Manage Shard Repository",
};

const modeToActionTitleMap = {
  [ShardRepositoryPanelMode.Add]: "Add Shard Repository",
  [ShardRepositoryPanelMode.SensitiveFieldsUpdate]: "Done",
  [ShardRepositoryPanelMode.FullEdit]: "Done",
  [ShardRepositoryPanelMode.LimitedEdit]: "Done",
};

export const formDefaultValue: IShardRepositoryFormValue = {
  storageType: null,
  region: "",
  isCustomRegion: true,
  endpointURL: "",
  bucket: "",
  account_name: "",
  container: "",
  project_id: "",
  key: "",
  secret: "",
  client_email: "",
  gcsConfigProps: null,
  sasUri: "",
};

export const ShardRepositoryPanel = connect(
  null,
  mapDispatchToProps,
)((props: IShardRepositoryPanelProps) => {
  const { mode, shardClusterRepository, accessCredentialsScope } = props;
  const [api, isLoading] = useApi();

  const [testConnectionResult, setTestConnectionResult] =
    useState<boolean>(null);
  const [isTestConnectionRequired, setIsTestConnectionRequired] =
    useState<boolean>(false);

  const [errorMessage, setErrorMessage] = useState<string>(null);
  const [storageNode, setStorageNode] = useState<IShardRepository>(null);
  const open =
    !!shardClusterRepository || mode === ShardRepositoryPanelMode.Add;
  const isNodeCreation = mode === ShardRepositoryPanelMode.Add;

  const [metrics, setMetrics] = useState<IEntityMetricsResponse>(null);

  const form: IShardRepositoryForm = {
    storageType: useField<StorageType>(formDefaultValue.storageType, [
      validators.required(),
    ]),
    region: useField<string>(formDefaultValue.region, [
      validateS3Region,
      validators.required(),
    ]),
    isCustomRegion: useField<boolean>(formDefaultValue.isCustomRegion),
    bucket: useField<string>(
      formDefaultValue.bucket,
      [validators.required()],
      [{ asyncValidator: validateBucketNameEndpointURL }],
    ),
    endpointURL: useField<string>(formDefaultValue.endpointURL, [
      validateEndpointURL,
    ]),
    container: useField<string>(
      formDefaultValue.container,
      [validators.required()],
      [{ asyncValidator: validateBucketNameEndpointURL }],
    ),
    account_name: useField<string>(formDefaultValue.account_name, [
      validators.required(),
    ]),
    project_id: useField<string>(formDefaultValue.project_id, [
      validators.required(),
    ]),
    key: useField<string>(formDefaultValue.key, [validators.required()]),
    secret: useField<string>(formDefaultValue.secret, [
      (value) =>
        isTestConnectionRequired ? validators.required()(value) : null,
    ]),
    client_email: useField<string>(formDefaultValue.client_email, [
      validators.required(),
    ]),
    gcsConfigProps: useField<IGCSConfigProps>(formDefaultValue.gcsConfigProps, [
      (value) =>
        props.mode === ShardRepositoryPanelMode.SensitiveFieldsUpdate
          ? validators.required()(value)
          : null,
    ]),
    sasUri: useField<string>(formDefaultValue.sasUri, [validators.required()]),
  };

  const testConnectionRequest = getTestConnectionRequest();

  function clearForm() {
    if (isNodeCreation) {
      formUtils.setFormValue<IShardRepositoryFormValue>(form, {
        ...formDefaultValue,
        storageType: form.storageType.value,
      });
    }
  }

  useEffect(
    () => {
      setErrorMessage(null);
      if (isTestConnectionRequired) {
        if (mode !== ShardRepositoryPanelMode.SensitiveFieldsUpdate) {
          setTestConnectionResult(null);
        } else {
          const isConnectionParamsChanged = formUtils.isChanged(form);
          if (isConnectionParamsChanged) {
            setIsTestConnectionRequired(true);
            setTestConnectionResult(null);
          }
        }
      }

      // eslint-disable-next-line
    },
    Object.values(formUtils.getFormValue(form, true)),
  );

  useEffect(() => {
    if (open) {
      if (shardClusterRepository) {
        setStorageNode(shardClusterRepository.shardRepository);
        setForm(shardClusterRepository.shardRepository);

        setIsTestConnectionRequired(false);
        setTestConnectionResult(null);

        if (mode === ShardRepositoryPanelMode.LimitedEdit) {
          formUtils.setIsDisabled(
            form,
            mode === ShardRepositoryPanelMode.LimitedEdit,
          );

          form.bucket.setIsDisabled(false);
          form.endpointURL.setIsDisabled(false);
          form.container.setIsDisabled(false);
          form.region.setIsDisabled(false);
          form.client_email.setIsDisabled(false);
          form.key.setIsDisabled(false);
          form.secret.setIsDisabled(false);

          // api(shardRepositoryService.getMetrics(shardClusterRepository.shardRepository.id))
          //     .then(metrics => {
          //       if (!!metrics && metrics.length > 0) {
          //         setMetrics(metrics[0]);
          //       }
          //     });
        }
      }

      if (mode === ShardRepositoryPanelMode.Add) {
        formUtils.setFormValue<IShardRepositoryFormValue>(
          form,
          formDefaultValue,
        );
        setIsTestConnectionRequired(false);
        setTestConnectionResult(null);

        form.storageType.setIsDisabled(false);
      }

      if (mode === ShardRepositoryPanelMode.SensitiveFieldsUpdate) {
        setIsTestConnectionRequired(true);
        setTestConnectionResult(null);

        form.storageType.setIsDisabled(true);
      }
    }

    // eslint-disable-next-line
  }, [open, shardClusterRepository]);

  useEffect(() => {
    let bucketField =
      form.storageType.value === "ABS" ? form.container : form.bucket;

    bucketField.validate(bucketField.value);

    // eslint-disable-next-line
  }, [form.endpointURL.value]);

  const showSensitiveFields =
    props.mode === ShardRepositoryPanelMode.SensitiveFieldsUpdate;

  function getTestConnectionRequest(): IShardRepositoryTestConnectionRequest {
    let formValue = storageUtils.cleanNotApplicableFields(
      formUtils.getFormValue<IShardRepositoryFormValue>(form),
      true,
    );
    return updateStorageNodeType(formValue);
  }

  // region validation checks

  function validateEndpointURL(endpointURL: string): string {
    let storageType = getStorageType(form.storageType.value, endpointURL);

    let isValid =
      storageType !== "s3p" ||
      endpointURL.startsWith("https://") ||
      endpointURL.startsWith("http://");

    return !isValid
      ? "Endpoint URL should start with http:// or https://"
      : null;
  }

  function validateBucketNameEndpointURL(bucketName: string): Promise<string> {
    let endpointURL = form.endpointURL.value;

    if (
      mode === ShardRepositoryPanelMode.FullEdit ||
      mode === ShardRepositoryPanelMode.LimitedEdit
    ) {
      if (
        props.shardClusterRepository.shardRepository.bucket === bucketName &&
        props.shardClusterRepository.shardRepository.endpointURL === endpointURL
      ) {
        return Promise.resolve(null);
      }
    }

    if (mode === ShardRepositoryPanelMode.SensitiveFieldsUpdate) {
      if (
        props.shardClusterRepository.shardRepository["bucket"] === bucketName
      ) {
        return Promise.resolve(null);
      }
    }

    let storageType = getStorageType(form.storageType.value, endpointURL);
    let bucketFieldName = storageType === "ABS" ? "Container" : "Bucket";

    if (!storageType) {
      return Promise.resolve(null);
    }

    return shardRepositoryService
      .bucketNameValidator(bucketName, storageType, endpointURL)
      .catch(catchException())
      .then((success) =>
        success
          ? null
          : storageType === "s3p"
            ? "Bucket and Endpoint URL combination should be unique"
            : `${bucketFieldName} should be unique`,
      );
  }

  function validateS3Region(region: string): string {
    let storageType = getStorageType(
      form.storageType.value,
      form.endpointURL.value,
    );
    if (storageType !== "S3") {
      return null;
    }

    if (!region) {
      return "Region is required for an S3 storage node";
    }
  }

  // endregion validation

  function setForm(storageNode: IShardRepository) {
    const storageType = storageUtils.getStorageProviderTypeGeneralizedType(
      storageNode.storageType,
    );
    let nodeProps = null;

    const bucketPropName = storageType === "ABS" ? "container" : "bucket";

    let savedStorageNodeData = {};

    if (mode === ShardRepositoryPanelMode.SensitiveFieldsUpdate) {
      savedStorageNodeData = shardRepositoryService.getStorageNodeState(
        storageNode.id,
      );
    }

    const formValue: IShardRepositoryFormValue = {
      [bucketPropName]: storageNode.bucket,
      storageType: storageType,
      key: "",
      secret: "",
      isCustomRegion: storageUtils.isCustomRegion(
        storageType,
        storageNode.region,
      ),
      region: storageNode.region,
      endpointURL: storageNode.endpointURL,
      gcsConfigProps: null,
      sasUri: "",
      ...nodeProps,
      ...savedStorageNodeData,
    };

    formUtils.setFormValue<IShardRepositoryFormValue>(form, formValue);
  }

  function addShardRepository(
    formValue: IShardRepositoryFormValue,
    updatedConnectionRes?: boolean,
  ) {
    return api(
      shardRepositoryService.addStorageNode(
        storageUtils.cleanNotApplicableFields(formValue, showSensitiveFields),
      ),
    ).then((savedShardRepository: any) => {
      if (savedShardRepository) {
        props.onRepositorySaved(
          savedShardRepository,
          {
            storeContent: true,
            storeKey: true,
            storeMetadata: true,
          },
          testConnectionResult || updatedConnectionRes,
        );
        props.onClose();
      }
    });
  }

  function updateShardRepository(formValue: IShardRepositoryFormValue) {
    return api(
      shardRepositoryService.update({
        ...storageUtils.cleanNotApplicableFields(
          formValue,
          showSensitiveFields,
        ),
        id: shardClusterRepository.shardRepository.id,
      }),
    ).then((savedShardRepository: any) => {
      if (savedShardRepository) {
        props.onRepositorySaved(
          savedShardRepository,
          shardClusterRepository.types,
          testConnectionResult,
        );
        props.onClose();
      }
    });
  }

  function updateNodeSensitiveFields(
    formValue: IShardRepositoryFormValue,
    updatedConnectionRes?: boolean,
  ) {
    let nodeProps = null;
    if (formValue.storageType === "GCS") {
      nodeProps = JSON.stringify(formValue.gcsConfigProps);
    }

    let node: IShardRepositoryRaw = {
      ...storageUtils.cleanNotApplicableFields(formValue, showSensitiveFields),
      id: String(shardClusterRepository.shardRepository.id),
      props: nodeProps,
    };

    if (!!formValue.container) {
      node.bucket = formValue.container;
    }
    if (node) {
      const save = () => {
        props.onRepositorySaved(
          node,
          {
            storeContent: true,
            storeKey: true,
            storeMetadata: true,
          },
          testConnectionResult || updatedConnectionRes,
        );
        props.onClose();
      };

      if (testConnectionResult || updatedConnectionRes) {
        shardRepositoryService.setStorageNodeState(
          shardClusterRepository.shardRepository.id,
          formValue,
        );
        save();
      }
    }
  }

  function getStorageType(
    type: StorageType,
    endpointURL?: string,
  ): StorageType {
    if (type === "S3" && !!endpointURL) {
      return "s3p";
    }

    return type;
  }

  function updateStorageNodeType<T extends IShardRepositoryFormValue>(
    formValue: T,
  ): T {
    return {
      ...formValue,
      storageType: getStorageType(formValue.storageType, formValue.endpointURL),
    };
  }

  function save() {
    if (
      !formUtils.isChanged(form) &&
      mode !== ShardRepositoryPanelMode.SensitiveFieldsUpdate
    ) {
      props.onClose();
      return;
    }

    if (!form.storageType.value) {
      setErrorMessage("Please select provider");
      form.storageType.validate(form.storageType.value);
      return;
    }

    formUtils.validateAll(form).then((isValid) => {
      if (isValid) {
        const formValue = updateStorageNodeType(
          formUtils.getFormValue<IShardRepositoryFormValue>(form),
        );
        if (
          mode === ShardRepositoryPanelMode.LimitedEdit ||
          (mode === ShardRepositoryPanelMode.FullEdit &&
            !isTestConnectionRequired)
        ) {
          if (form.bucket.isChanged || form.region.isChanged) {
            props.openUpdateConfirmation({
              originalNode: shardClusterRepository.shardRepository,
              updatedFields: {
                bucket: form.bucket.value,
                region: form.region.value,
              },
            });
          } else {
            updateShardRepository(formValue);
          }
        }

        const isAddNode: boolean =
          mode === ShardRepositoryPanelMode.Add ||
          mode === ShardRepositoryPanelMode.FullEdit;
        const isUpdateSensitiveFields: boolean =
          mode === ShardRepositoryPanelMode.SensitiveFieldsUpdate;

        if (isTestConnectionRequired && testConnectionResult == null) {
          testConnection(getTestConnectionRequest()).then((res) => {
            if (isAddNode) {
              addShardRepository(formValue, res);
            } else if (isUpdateSensitiveFields) {
              updateNodeSensitiveFields(formValue, res);
            }
          });
        } else {
          if (isAddNode) {
            addShardRepository(formValue);
          } else if (isUpdateSensitiveFields) {
            updateNodeSensitiveFields(formValue);
          }
        }
      }
    });
  }

  // region update confirmation and cancel

  function handleUpdateConfirm() {
    if (mode === ShardRepositoryPanelMode.LimitedEdit) {
      const formValue = updateStorageNodeType(
        formUtils.getFormValue<IShardRepositoryFormValue>(form),
      );
      updateShardRepository(formValue);
    }
  }

  function handleUpdateCancel() {
    if (mode === ShardRepositoryPanelMode.LimitedEdit) {
      const formValue = formUtils.getFormValue<IShardRepositoryFormValue>(form);
      const storageType = storageUtils.getStorageProviderTypeGeneralizedType(
        shardClusterRepository.shardRepository.storageType,
      );

      const revertedFormValue = {
        ...formValue,
        bucket: shardClusterRepository.shardRepository.bucket,
        region: shardClusterRepository.shardRepository.region,
        isCustomRegion: storageUtils.isCustomRegion(
          storageType,
          storageNode.region,
        ),
      };

      formUtils.setFormValue<IShardRepositoryFormValue>(
        form,
        revertedFormValue,
      );
    }
  }

  // endregion

  function testConnection(
    testConnectionRequest: IShardRepositoryTestConnectionRequest,
  ) {
    return api(
      shardRepositoryService.testConnection(testConnectionRequest),
    ).then((result: IShardRepositoryTestConnectionResponse) => {
      let connectionResult = result.isConnectionSuccessful;
      let isExcessivePermission = false;

      if (!!accessCredentialsScope) {
        const allPermissions = enumUtils.values(
          AccessCredentialsScopePermission,
        ) as AccessCredentialsScopePermission[];

        connectionResult = scopePermissionDict[accessCredentialsScope].every(
          (p) => result.operationStatus[p],
        );
        isExcessivePermission =
          accessCredentialsScope !== "full" &&
          allPermissions
            .filter(
              (x) => !scopePermissionDict[accessCredentialsScope].includes(x),
            )
            .some((p) => result.operationStatus[p]);
      }

      setTestConnectionResult(connectionResult);
      if (connectionResult) {
        setErrorMessage(null);
      } else {
        props.showSnackBar(result.msg);
      }

      if (isExcessivePermission || !connectionResult) {
        props.openCredentialsInfo({
          isConnectionSuccessful: connectionResult,
          operationStatus: result.operationStatus,
          scope: accessCredentialsScope,
        });
      }

      return result.isConnectionSuccessful;
    });
  }

  return (
    <>
      <DetailsPanel
        open={open}
        isLoading={isLoading}
        onCloseClick={props.onClose}
        title={modeToTitleMap[mode]}
        className={styles.panel}
        autoFocus={true}
      >
        {open && (
          <FormPanel
            okButtonText={modeToActionTitleMap[mode]}
            okButtonClick={save}
            errorMessage={errorMessage}
          >
            {!isNodeCreation && !!storageNode && (
              <>
                <OwnerInfoSection entity={storageNode} />
                <div>Id: {storageNode?.id}</div>
                <div>
                  Created At:{" "}
                  {Formatters.formatDateTime(storageNode?.createdAt)}
                </div>

                <MetricsInfo
                  entityName="Shard Repository"
                  metrics={metrics}
                  className={styles.metrics}
                />
              </>
            )}

            {isNodeCreation && (
              <div className={styles.description}>
                A Shard Repository is a dedicated storage location designed to
                hold data, key, and metadata shards. It enhances data management
                by allowing users to bring their own storage solutions, ensuring
                flexibility and security while maintaining easy access to their
                information.
              </div>
            )}

            <ShardRepositorySection
              form={form}
              isTestConnectionSuccess={testConnectionResult}
              testConnectionRequest={testConnectionRequest}
              onTestConnection={testConnection}
              showSensitiveFields={showSensitiveFields}
              clearForm={clearForm}
            />
          </FormPanel>
        )}
      </DetailsPanel>

      <UpdateConfirmation
        onUpdate={handleUpdateConfirm}
        onCancel={handleUpdateCancel}
      />
    </>
  );
});
